import { observer } from 'mobx-react-lite'
import { LINK_COMPLIANCE_LEARN_MORE_LINK } from 'shared/constants/links'
import { Alert } from 'shared/ui'
import { EnumPlanTypes } from 'entities/Compliance'
import { DropdownCards } from 'entities/Billing/ui/DropdownCards'
import { LearnMoreLink } from 'pages/settings/ui/LearnMoreLink/LearnMoreLink'
import { useLocalNumbersStepsContext } from 'pages/settings/pages/compliance/pages/localNumbers/context/localNumbersStepsContext'
import { summaryData } from 'pages/settings/pages/compliance/pages/localNumbers/pages/OrderSummary/lib/constants'
import { PageDescription } from 'pages/settings/ui/PageDescription/PageDescription'
import { NextButton } from 'pages/settings/ui/NextButton/NextButton'
import { RegistrationErrorAlert } from 'pages/settings/pages/compliance/pages/localNumbers/ui/RegistrationErrorAlert/RegistrationErrorAlert'
import { AiCheckerNextButtons } from 'pages/settings/pages/compliance/ui/AiCheckerNextButton/AiCheckerNextButtons'
import { SummaryLine } from 'pages/settings/ui/SummaryLine/SummaryLine'
import styles from './styles.module.scss'

export const OrderSummary = observer(() => {
  const {
    stepLoading,
    continueWithIssuesLoading,
    setContinueWithIssuesLoading,
    isSubmitDisabled,
    dropdownCardsStore,
    packageStore,
    onSubmit,
    checkAndSubmitLoading,
    setCheckAndSubmitLoading,
    paymentError,
    complianceLayerStore,
    aiSidebarStore: {
      isAiCheckrWillUse,
      isLoading: aiLoading,
      isSuccessButton,
      isErrorButton,
      isContinueWithIssues,
    },
  } = useLocalNumbersStepsContext()

  const { complianceServiceBrand, complianceServiceCampaign } = complianceLayerStore
  const noFailedBrand = !complianceServiceBrand?.isStatusFailed
  const noFailedCampaign = !complianceServiceCampaign?.isStatusFailed
  const summaryValues =
    packageStore.activePlanType === EnumPlanTypes.STANDARD
      ? summaryData.public
      : summaryData.private

  const formatCurrency = (value: number) => {
    return `$${value.toFixed(2)}`
  }

  const getTotal = () => {
    let total = 0

    if (noFailedBrand && noFailedCampaign) {
      total += summaryValues.brandRegistrationFeeCount
      total += summaryValues.campaignVerificationFeeCount

      return formatCurrency(total)
    }

    if (complianceServiceBrand?.isStatusFailed && !complianceServiceBrand?.isInternalRejected) {
      total += summaryValues.brandRegistrationFeeCount
    }

    if (
      (complianceServiceCampaign?.isStatusFailed || complianceServiceBrand?.isStatusFailed) &&
      !complianceServiceCampaign?.isInternalRejected
    ) {
      total += summaryValues.campaignVerificationFeeCount
    }

    return formatCurrency(total)
  }

  const showBrandFee = () => {
    if (noFailedBrand && noFailedCampaign) return true

    return complianceServiceBrand?.isStatusFailed && !complianceServiceBrand?.isInternalRejected
  }

  const showCampaignFee = () => {
    if (noFailedBrand && noFailedCampaign) return true

    return (
      (complianceServiceCampaign?.isStatusFailed || complianceServiceBrand?.isStatusFailed) &&
      !complianceServiceCampaign?.isInternalRejected
    )
  }

  return (
    <div className={styles.orderSummary}>
      <PageDescription>
        {
          'The fees below are imposed by the carrier to register your brand and use case. Salesmsg passes down these fees with no markup. These fees are non-refundable.'
        }
        <LearnMoreLink href={LINK_COMPLIANCE_LEARN_MORE_LINK}>Learn more</LearnMoreLink>
      </PageDescription>

      <RegistrationErrorAlert />

      {paymentError && (
        <Alert
          className={styles.alert}
          item={{
            type: 'error',
            title: 'We were unable to process your payment',
            desc: 'Please select a different card',
          }}
        />
      )}

      <div className={styles.summary}>
        {showBrandFee() && (
          <SummaryLine
            label='Brand registration fee (one-time)'
            value={summaryValues.brandRegistrationFee}
            tooltip={{
              label: 'This is a one-time carrier fee for registering your Brand',
            }}
          />
        )}
        {showCampaignFee() && (
          <SummaryLine
            label='Campaign verification fee (one-time)'
            value={summaryValues.campaignVerificationFee}
            tooltip={{
              label: 'This is a one-time carrier fee for registering your brand campaign use case',
            }}
          />
        )}
        {showCampaignFee() && (
          <SummaryLine
            label='Campaign use case fee (recurring)'
            value={summaryValues.useCaseFee}
            tooltip={{
              width: 275,
              label:
                'This is a monthly recurring carrier fee for registering your campaign use case.',
              desc: 'It will be charged after your campaign is approved',
            }}
          />
        )}
        <div className={styles.divider} />
        <SummaryLine label='Total' value={getTotal()} isTotal />
      </div>
      {dropdownCardsStore && <DropdownCards store={dropdownCardsStore} />}

      <div className={styles.stickyActions}>
        {isAiCheckrWillUse ? (
          <AiCheckerNextButtons
            loading={aiLoading}
            isSubmitLoading={checkAndSubmitLoading}
            disabled={isSubmitDisabled}
            text='Check and submit'
            onClick={() => {
              setCheckAndSubmitLoading(true)
              onSubmit(true)
            }}
            icon='stars'
            isSuccess={isSuccessButton}
            isError={isErrorButton}
            isContinueWithIssues={isContinueWithIssues}
            onContinueWithIssuesClick={() => {
              setContinueWithIssuesLoading(true)
              onSubmit()
            }}
            isContinueWithIssuesLoading={continueWithIssuesLoading}
            continueWithIssuesText='Submit anyway'
          />
        ) : (
          <NextButton
            loading={stepLoading}
            disabled={isSubmitDisabled || stepLoading}
            text='Submit my application'
            onClick={() => onSubmit()}
          />
        )}
      </div>
    </div>
  )
})
